import classNames from 'classnames'
import React from 'react'

import { CheckboxGroup, ColorFilterGroup, DropdownV2, RadioButtonGroup } from '@src/quince-components/src/atoms'

import { FILTER_DROPDOWN_TYPES, SORT_OPTIONS } from '../constants/constants'
import { CheckboxOption } from '../menu/filterSection.interface'
import desktopStyles from '../menu/filterSectionWithDrawer/filterSectionWithDrawer.module.scss' // Desktop styles
import styles from '../menu/mobileFilters/mobileFilters.module.scss' // Base styles

interface FilterDropdownProps {
  type: FILTER_DROPDOWN_TYPES
  label: React.ReactNode
  isActive?: boolean
  options?: any[]
  selectedValues?: string[]
  value?: string
  onTriggerClick?: (e: React.MouseEvent, isOpen: boolean) => void
  onChange?: (value: any) => void
  placement?: 'bottomLeft' | 'bottom' | 'bottomRight'
  buttonClassName?: string
  menuClassName?: string
  items?: any[]
  className?: string
  isMobile?: boolean
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  type,
  label,
  isActive = false,
  options = [],
  selectedValues = [],
  value = '',
  onTriggerClick,
  onChange,
  placement = 'bottomLeft',
  buttonClassName,
  menuClassName,
  items = [],
  className,
  isMobile = true,
}) => {
  // Handler for checkbox options
  const handleCheckboxChange = (selectedOptions: CheckboxOption[]) => {
    if (onChange) {
      onChange(selectedOptions)
    }
  }

  // Handler for color selection
  const handleColorChange = (value: string) => {
    if (onChange) {
      onChange(value)
    }
  }

  // Handler for sort selection
  const handleSortChange = (value: string) => {
    if (onChange) {
      onChange(value)
    }
  }

  // Use appropriate styles based on viewport
  const styleSet = isMobile ? styles : desktopStyles

  // Conditional rendering based on filter type
  const renderDropdownContent = () => {
    switch (type) {
      case FILTER_DROPDOWN_TYPES.CHECKBOX:
        return {
          items: [
            {
              key: 'checkbox-filter',
              label: (
                <CheckboxGroup
                  options={options as CheckboxOption[]}
                  onChange={handleCheckboxChange}
                  direction="column"
                  maxVisible={8}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
          className: menuClassName,
        }

      case FILTER_DROPDOWN_TYPES.COLOR:
        return {
          items: [
            {
              key: 'colors-filter',
              label: (
                <ColorFilterGroup
                  options={options}
                  selectedValues={selectedValues}
                  onChange={handleColorChange}
                  direction="horizontal"
                  circleClassName={styleSet.colorFilterCircle}
                  className={styleSet.colorFilterGroup}
                />
              ),
            },
          ],
          className: menuClassName,
        }

      case FILTER_DROPDOWN_TYPES.SORT:
        return {
          items: [
            {
              key: 'sort-options',
              label: (
                <RadioButtonGroup
                  name="sort-options"
                  options={SORT_OPTIONS.map(option => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={value}
                  onChange={handleSortChange}
                  className={styleSet.sortRadioGroup}
                  itemsClassName={styleSet.sortRadioItems}
                  radioItemClassName={styleSet.sortRadioItem}
                  radioLabelClassName={styleSet.sortRadioLabel}
                />
              ),
            },
          ],
          className: menuClassName ?? styleSet.sortDropdownMenu,
        }

      case FILTER_DROPDOWN_TYPES.CARD:
        return {
          items,
          className: menuClassName,
        }

      default:
        return {
          items: [],
          className: menuClassName,
        }
    }
  }
  return (
    <DropdownV2
      menu={renderDropdownContent()}
      trigger={['click']}
      placement={placement}
      autoAdjustOverflow={false}
      isActive={isActive}
      multiSelect={type === 'checkbox' || type === 'color'}
      buttonClassName={classNames(
        isMobile ? styles.mobileFilterButton : desktopStyles.mobileFilterButton,
        buttonClassName,
      )}
      onTriggerClick={onTriggerClick}
      className={className}
    >
      {label}
    </DropdownV2>
  )
}
